import { createFileRoute } from '@tanstack/react-router'
import { createElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHotkeys } from 'react-hotkeys-hook'
import { NotificationList } from '../components/NotificationList'
import { NotificationDetailsPanel } from '../components/NotificationDetailsPanel'
import { NotificationsEmptyState } from '../components/NotificationsEmptyState'
import { useNotificationActions, useNotificationLookups, useNotifications } from '../hooks'
import {
  buildInboxGroups,
  countUnread,
  entryContains,
  entryNotifications,
  entryPrimary,
  filterByTab,
  flattenEntries,
} from '../utils/notificationInbox'
import type { NotificationTab } from '../utils/notificationInbox'
import type { NotificationEntry } from '../types'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { usePageLayout } from '@/hooks/usePageLayout'
import { useIsMobile } from '@/hooks/useMobile'
import TaskSidebar from '@/components/app/sidebars/task'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/notifications/',
)({
  component: NotificationListPage,
})

/** Re-render relative times ("12m") once a minute. */
function useNow(intervalMs = 60_000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

/**
 * Notifications (design 17): a triage inbox for things that involve you.
 *
 * Left: rows grouped Today / Yesterday / Earlier, several updates on one task
 * stacked into one row, tabs All / Mentions / Assigned. Right: the task's
 * conversation with the triggering comment highlighted, where you can reply,
 * react, mark Done (E) or Unfollow without leaving the inbox. ↑/↓ (or j/k)
 * move. Mobile shows the list and opens the pane in a bottom sheet.
 *
 * Route: `/$workspaceSlug/notifications/`
 */
function NotificationListPage() {
  const { t } = useTranslation('notifications')
  const { notifications, isLoading } = useNotifications()
  const lookups = useNotificationLookups()
  const { markManyAsRead, markAllAsRead, markDone } = useNotificationActions()
  const isMobile = useIsMobile()
  const now = useNow()

  const [tab, setTab] = useState<NotificationTab>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  usePageLayout({
    sidebarContent: createElement(TaskSidebar),
  })

  const unreadCount = useMemo(() => countUnread(notifications), [notifications])
  const groups = useMemo(
    () => buildInboxGroups(filterByTab(notifications, tab), now),
    [notifications, tab, now],
  )
  const entries = useMemo(() => flattenEntries(groups), [groups])
  const selectedEntry = useMemo(
    () => (selectedId ? entries.find((e) => entryContains(e, selectedId)) : undefined),
    [entries, selectedId],
  )

  // Desktop keeps a row selected: the first one, or the first again when the
  // selection disappears (e.g. dismissed on another device).
  useEffect(() => {
    if (isMobile) {
      if (selectedId && !selectedEntry) setIsSheetOpen(false)
      return
    }
    if (!selectedEntry) {
      const first = entries[0]
      setSelectedId(first ? entryPrimary(first).id : null)
    }
  }, [entries, isMobile, selectedEntry, selectedId])

  const select = useCallback(
    (entry: NotificationEntry) => {
      setSelectedId(entryPrimary(entry).id)
      void markManyAsRead(entryNotifications(entry))
      if (isMobile) setIsSheetOpen(true)
    },
    [isMobile, markManyAsRead],
  )

  const toggleExpand = useCallback((entryId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(entryId)) next.delete(entryId)
      else next.add(entryId)
      return next
    })
  }, [])

  const handleDone = useCallback(
    (toDismiss: ReadonlyArray<NotificationDocument>) => {
      const first = toDismiss[0]
      if (!first) return
      const index = entries.findIndex((e) => entryContains(e, first.id))
      const next = index >= 0 ? (entries[index + 1] ?? entries[index - 1]) : undefined
      setSelectedId(next ? entryPrimary(next).id : null)
      if (isMobile) setIsSheetOpen(false)
      void markDone(toDismiss)
    },
    [entries, isMobile, markDone],
  )

  const move = useCallback(
    (delta: 1 | -1) => {
      if (entries.length === 0) return
      const index = selectedEntry ? entries.indexOf(selectedEntry) : -1
      const target = entries[Math.min(entries.length - 1, Math.max(0, index + delta))]
      if (target && target !== selectedEntry) select(target)
    },
    [entries, select, selectedEntry],
  )

  const hotkeysEnabled = !isMobile && entries.length > 0
  useHotkeys('down, j', (e) => { e.preventDefault(); move(1) }, { enabled: hotkeysEnabled }, [move])
  useHotkeys('up, k', (e) => { e.preventDefault(); move(-1) }, { enabled: hotkeysEnabled }, [move])
  useHotkeys(
    'e',
    (e) => {
      if (!selectedEntry) return
      e.preventDefault()
      handleDone(entryNotifications(selectedEntry))
    },
    { enabled: hotkeysEnabled },
    [handleDone, selectedEntry],
  )

  if (!isLoading && notifications.length === 0) {
    return <NotificationsEmptyState />
  }

  const list = (
    <NotificationList
      groups={groups}
      lookups={lookups}
      now={now}
      tab={tab}
      onTabChange={setTab}
      unreadCount={unreadCount}
      onMarkAllRead={() => void markAllAsRead(notifications)}
      selectedEntryId={selectedEntry?.id ?? null}
      expandedEntryIds={expanded}
      onSelect={select}
      onToggleExpand={toggleExpand}
      isLoading={isLoading}
      showHints={!isMobile}
    />
  )

  const pane = (
    <NotificationDetailsPanel
      notification={selectedEntry ? entryPrimary(selectedEntry) : undefined}
      notifications={selectedEntry ? entryNotifications(selectedEntry) : undefined}
      lookups={lookups}
      onDone={handleDone}
      showHints={!isMobile}
    />
  )

  if (isMobile) {
    return (
      <>
        <div className="flex h-full min-h-0 w-full flex-col">{list}</div>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetContent side="bottom" className="h-[85vh] gap-0 p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>{t('pane.sheetTitle')}</SheetTitle>
            </SheetHeader>
            <div className="h-full min-h-0 overflow-hidden pt-6">{pane}</div>
          </SheetContent>
        </Sheet>
      </>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full">
      <ResizablePanelGroup orientation="horizontal" className="h-full w-full flex-1">
        <ResizablePanel defaultSize={36} minSize={26} className="min-h-0">
          {list}
        </ResizablePanel>
        <ResizableHandle className="bg-transparent" />
        <ResizablePanel defaultSize={64} minSize={36} className="min-h-0 p-3 pl-1">
          <div className="h-full min-h-0 overflow-hidden rounded-xl border bg-card">{pane}</div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

export default NotificationListPage
